import React, { useContext, useCallback } from 'react'
import _ from 'lodash'
import { useDispatch } from 'react-redux'
import { v4 } from 'uuid'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

import { addAction, updateActionsOrder } from '../../actions/flow'

import MessageEdit from './actions/edit/MessageEdit'

import { StepContext } from '../contexts'
import Message from './actions/Message'
import QuickReplies from './actions/QuickReplies'
import Guide from './actions/Guide'
import Answer from './actions/Answer'

import ActionPill from './actions/templates/ActionPill'

import actionDetails from './actions/meta.json'
import Options from './Options'

const Action = (props) => {
  const { id, index = 0, type } = props

  switch (type) {
    case 'message':
      return <Message actionId={id} index={index} />

    case 'quickReplies':
      return <QuickReplies actionId={id} index={index} />

    case 'guide':
      return <Guide actionId={id} index={index} />

    case 'answer':
      return <Answer actionId={id} index={index} />

    case 'form':
      return <ActionPill actionId={id} text='Form' index={index} />

    case 'ml_search':
      return <ActionPill actionId={id} text={actionDetails[type].name} editable index={index} />

    case 'happyfoxchat_transfer_to_department':
      return <ActionPill actionId={id} text={actionDetails[type].name} editable index={index} />

    case 'happyfoxchat_contact_details_form':
      return <ActionPill actionId={id} text={actionDetails[type].name} index={index} editable />

    case 'happyfoxchat_get_name':
    case 'happyfoxchat_get_email':
    case 'happyfoxchat_get_phone':
    case 'happyfoxchat_get_description':
    case 'happyfoxchat_get_attachments':
      return <ActionPill actionId={id} text='Collect Information (HFC)' editable index={index} />

    case 'collect_information':
    case 'collect_information_name':
    case 'collect_information_email':
    case 'collect_information_text':
    case 'collect_information_text_multiline':
      return <ActionPill actionId={id} text='Collect Information' editable index={index} />

    case 'happyfox_workflow_action': {
      return <ActionPill actionId={id} text='Trigger Workflow' index={index} editable />
    }

    case 'zapier_trigger_form_submit':
    case 'zapier_trigger_quick_reply_submit': {
      return <ActionPill index={index} actionId={id} text={actionDetails[type].name} editable />
    }

    case 'end_chat':
      return <ActionPill actionId={id} text={actionDetails[type].name} deletable index={index} />

    default:
      return null
  }
}

const Actions = () => {
  const { stepId, step } = useContext(StepContext)
  const dispatch = useDispatch()

  const actions = _.get(step, 'actions', [])
  const settings = _.get(step, 'settings', {})

  let hasActions = _.get(actions, 'length', 0) > 0

  const onMessageSave = useCallback(
    ({ text }) => {
      dispatch(
        addAction({
          stepId,
          actionId: v4(),
          setting: {
            type: 'message',
            text
          }
        })
      )
    },
    [dispatch, stepId]
  )

  const handleDragEnd = (event) => {
    const { over, active } = event

    if (!over) {
      return
    }

    const sourceIndex = actions.findIndex((id) => id === active.id)
    const destinationIndex = actions.findIndex((id) => id === over.id)

    dispatch(updateActionsOrder({ stepId, sourceIndex, destinationIndex }))
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors} modifiers={[restrictToVerticalAxis]}>
      <div>
        <SortableContext items={actions} type='ACTIONS'>
          {!hasActions && (
            <div className='quote-wrapper'>
              <div className='quote-item'>
                <MessageEdit isFirstAction saveFn={onMessageSave} mode='create' />
              </div>
            </div>
          )}

          {_.map(actions, (actionId, index) => {
            const action = _.get(settings, `${actionId}`)

            hasActions =
              action.type !== 'ml_search' &&
              action.type !== 'collect_information' &&
              action.type !== 'collect_information_email'
            return <Action id={actionId} key={`Step_Action__${actionId}`} type={action.type} index={index} />
          })}

          {hasActions && <Options stepId={stepId} />}
        </SortableContext>
      </div>
    </DndContext>
  )
}

export default Actions
